import { Badge } from "@thilke/ui/components/badge";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

type RollOutcome = "default" | "success" | "failure" | "critical";

type DieTone = "default" | "min" | "max" | "selected";

export interface DieResult {
  id?: string;
  value: React.ReactNode;
  sides?: number | string;
  label?: React.ReactNode;
  tone?: DieTone;
}

export interface DamageTableEntry {
  id?: string;
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface RollCardProps
  extends Omit<React.ComponentProps<"article">, "title"> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  speaker?: React.ReactNode;
  formula?: React.ReactNode;
  total?: React.ReactNode;
  outcome?: RollOutcome;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

const outcomeBorderClass: Record<RollOutcome, string> = {
  default: "border-border",
  success: "border-success/70",
  failure: "border-destructive/70",
  critical: "border-warning/80",
};

const outcomeTotalClass: Record<RollOutcome, string> = {
  default: "border-border bg-background text-foreground",
  success: "border-success/40 bg-success/10 text-success",
  failure: "border-destructive/40 bg-destructive/10 text-destructive",
  critical: "border-warning/50 bg-warning/10 text-warning",
};

const dieToneClass: Record<DieTone, string> = {
  default: "border-border bg-muted text-foreground",
  min: "border-destructive/50 bg-destructive/10 text-destructive",
  max: "border-success/50 bg-success/10 text-success",
  selected: "border-primary/60 bg-primary/15 text-primary",
};

function RollCard({
  title,
  subtitle,
  speaker,
  formula,
  total,
  outcome = "default",
  meta,
  actions,
  children,
  className,
  ...props
}: RollCardProps) {
  return (
    <article
      data-slot="roll-card"
      data-outcome={outcome}
      className={cn(
        "grid gap-2.5 rounded-md border bg-card p-3 text-card-foreground shadow-sm",
        outcomeBorderClass[outcome],
        className,
      )}
      {...props}
    >
      <header
        data-slot="roll-card-header"
        className="grid grid-cols-[1fr_auto] items-start gap-2"
      >
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <h3
              data-slot="roll-card-title"
              className="min-w-0 truncate text-sm font-semibold leading-tight"
            >
              {title}
            </h3>
            {speaker ? <Badge variant="outline">{speaker}</Badge> : null}
            {formula ? <Badge variant="secondary">{formula}</Badge> : null}
          </div>
          {subtitle ? (
            <p
              data-slot="roll-card-subtitle"
              className="mt-1 text-xs leading-snug text-muted-foreground"
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-start gap-1.5">
          {meta ? (
            <div
              data-slot="roll-card-meta"
              className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs leading-none text-muted-foreground"
            >
              {meta}
            </div>
          ) : null}
          {total ? (
            <div
              data-slot="roll-card-total"
              className={cn(
                "min-w-8 rounded-md border px-2 py-1 text-center font-mono text-sm font-semibold leading-none",
                outcomeTotalClass[outcome],
              )}
            >
              {total}
            </div>
          ) : null}
        </div>
      </header>
      {children ? (
        <div
          data-slot="roll-card-body"
          className="grid gap-2 rounded-md border border-border bg-background/70 p-2.5 text-sm"
        >
          {children}
        </div>
      ) : null}
      {actions ? (
        <footer
          data-slot="roll-card-actions"
          className="flex flex-wrap items-center justify-end gap-1"
        >
          {actions}
        </footer>
      ) : null}
    </article>
  );
}

function DiceBreakdown({
  formula,
  total,
  dice,
  className,
  ...props
}: React.ComponentProps<"section"> & {
  formula: React.ReactNode;
  total: React.ReactNode;
  dice: DieResult[];
}) {
  return (
    <section
      data-slot="dice-breakdown"
      className={cn("grid gap-1.5", className)}
      {...props}
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <span
          data-slot="dice-breakdown-formula"
          className="min-w-0 truncate font-mono text-muted-foreground"
        >
          {formula}
        </span>
        <span
          data-slot="dice-breakdown-total"
          className="font-mono font-semibold text-foreground"
        >
          {total}
        </span>
      </div>
      <ol
        data-slot="dice-breakdown-rolls"
        className="flex min-w-0 flex-wrap gap-1"
      >
        {dice.map((die) => (
          <li
            key={die.id ?? String(die.label ?? die.sides ?? die.value ?? "die")}
          >
            <Die {...die} />
          </li>
        ))}
      </ol>
    </section>
  );
}

function Die({
  value,
  sides = 6,
  label,
  tone = "default",
  className,
  ...props
}: React.ComponentProps<"span"> & DieResult) {
  return (
    <span
      data-slot="die"
      data-tone={tone}
      className={cn(
        "inline-grid size-7 shrink-0 place-items-center rounded-[calc(var(--radius)-1px)] border font-mono text-xs font-semibold leading-none shadow-sm",
        dieToneClass[tone],
        className,
      )}
      title={typeof label === "string" ? label : `d${sides}`}
      {...props}
    >
      {value}
    </span>
  );
}

function DamageTable({
  entries,
  selectedIndex,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  entries: DamageTableEntry[];
  selectedIndex?: number;
}) {
  return (
    <div
      data-slot="damage-table"
      className={cn("grid gap-1 overflow-x-auto", className)}
      {...props}
    >
      <div className="grid min-w-[18rem] grid-cols-7 gap-1 text-center text-[11px] font-semibold text-muted-foreground">
        {entries.map((entry, index) => (
          <div
            key={`label-${entry.id ?? String(entry.label)}`}
            data-slot="damage-table-label"
            className={cn(
              "rounded-[calc(var(--radius)-2px)] border border-border bg-muted px-1 py-1 leading-none",
              selectedIndex === index && "border-primary text-primary",
            )}
          >
            {entry.label}
          </div>
        ))}
      </div>
      <div className="grid min-w-[18rem] grid-cols-7 gap-1 text-center font-mono text-xs">
        {entries.map((entry, index) => (
          <div
            key={`value-${entry.id ?? String(entry.label)}`}
            data-slot="damage-table-value"
            className={cn(
              "rounded-[calc(var(--radius)-2px)] border border-border bg-background px-1 py-1 font-semibold leading-none",
              selectedIndex === index &&
                "border-primary bg-primary/10 text-primary shadow-sm",
            )}
          >
            {entry.value}
          </div>
        ))}
      </div>
    </div>
  );
}

export { DamageTable, DiceBreakdown, Die, RollCard, type RollOutcome };
